import TextField from "@mui/material/TextField";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { MenuItem } from "@mui/material";

import { useSelector } from "react-redux";

import { Icon } from "@/components";

import {
	subTasksSelector,
	tasksSelector,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import { sharedInputProps, sharedMenuProps, sharedSx } from "@/styles/sharedComponentStyles";

import { canMoveTo, statusBadgeClass } from "../../../TasksAndSubTasks/statusStyles";

import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { useToDoSocket } from "../providers/SocketContext";
import AssigneeModal from "../../../TasksAndSubTasks/components/AssigneeModal";

interface ToDoStatusDropDownProps {
	companyToDoProgressId: number;
	className?: string;
	isTask?: boolean;
}

export function ToDoStatusDropDown({
	companyToDoProgressId,
	className,
	isTask,
}: ToDoStatusDropDownProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const { changeTaskStatus, changeSubTaskStatus } = useToDoSocket();
	const tasks = useSelector(tasksSelector);
	const subtasks = useSelector(subTasksSelector);
	const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);

	const data = useMemo(
		() =>
			isTask
				? tasks.find((item) => item.id === companyToDoProgressId)
				: subtasks.find((item) => item.id === companyToDoProgressId),
		[companyToDoProgressId, tasks, subtasks]
	);
	/**
	 * A subtask moves with its own socket event and needs its parent's id — the
	 * task event silently does nothing when handed a subtask id.
	 */
	const parentTaskId = data && "taskId" in data ? data.taskId : undefined;

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newStatus = e.target.value as ToDoStatus;

			if (newStatus === ToDoStatus.QA) {
				setIsAssigneeModalOpen(true);

				return;
			}

			if (isTask) {
				changeTaskStatus(companyToDoProgressId, newStatus);

				return;
			}

			if (parentTaskId) {
				changeSubTaskStatus(parentTaskId, companyToDoProgressId, newStatus);
			}
		},
		[changeTaskStatus, changeSubTaskStatus, companyToDoProgressId, isTask, parentTaskId]
	);

	const currentStatus = data?.status as ToDoStatus | undefined;
	const isDoneBlocked = !!currentStatus && !canMoveTo(currentStatus, ToDoStatus.DONE);

	return (
		<>
			<div className="w-full flex flex-col gap-2">
				<div className="flex flex-col gap-1.5">
					<p className="text-[12px] font-bold uppercase tracking-wide leading-4 text-subtle">
						{t("createTask.status")}
					</p>
					<span
						className={`w-fit rounded-pill px-2.5 py-1 text-[12px] font-semibold leading-4 ${
							currentStatus ? statusBadgeClass[currentStatus] : "bg-surface text-subtle"
						}`}
					>
						{ts(`${currentStatus}`)}
					</span>
				</div>

				<div className="flex flex-col gap-1.5">
					<p className="text-[12px] font-bold uppercase tracking-wide leading-4 text-subtle">
						{ts("moveTo")}
					</p>
					<TextField
						select
						SelectProps={{
							renderValue: (selected) => <>{ts(`${selected}`)}</>,
							MenuProps: sharedMenuProps,
						}}
						className={className}
						helperText={
							isDoneBlocked
								? isTask
									? ts("cannot-move-task-to-done")
									: ts("cannot-move-subtask-to-done")
								: undefined
						}
						inputProps={sharedInputProps}
						size="small"
						sx={{ ...sharedSx, width: "100%" }}
						value={currentStatus ?? ""}
						onChange={onChange}
					>
						{Object.values(ToDoStatus).map((status) => (
							<MenuItem key={status} disabled={!canMoveTo(currentStatus, status)} value={status}>
								{ts(`${status}`)}
								{currentStatus === status && <Icon className="ml-2 w-4 h-4" icon={faCheck} />}
							</MenuItem>
						))}
					</TextField>
				</div>
			</div>

			<AssigneeModal
				data={data ?? null}
				isOpen={isAssigneeModalOpen}
				setIsOpen={(val) => {
					setIsAssigneeModalOpen(val);
				}}
			/>
		</>
	);
}
